import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { isRtlLanguage } from 'i18n'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/solid'
import moment from 'moment'

import SelectField from 'components/Common/SelectField'
import { CalendarField } from 'components/Calendar/style'
import { calendarOptions } from 'config'

const Toolbar = ({ view, setDate, date, setView, ...toolbar }) => {
  const { t, i18n } = useTranslation()
  // In RTL the back control sits to the right of the label and next to its left,
  // so the glyphs have to swap too — otherwise each arrow points away from the
  // direction it actually navigates.
  const isRtl = isRtlLanguage(i18n.language)
  const BackIcon = isRtl ? ChevronRightIcon : ChevronLeftIcon
  const NextIcon = isRtl ? ChevronLeftIcon : ChevronRightIcon
  const localizedCalendarOptions = useMemo(
    () =>
      calendarOptions.map((opt) => ({
        ...opt,
        label: t(`calendar.${opt.label.toLowerCase()}`, opt.label),
      })),
    [t]
  )
  const goToBack = () => {
    let newDate: any = ''
    if (view === 'month') {
      newDate = moment(date).subtract(1, 'months').startOf('month')
    } else if (view === 'week') {
      newDate = moment(date).subtract(1, 'week')
    } else if (view === 'day') {
      newDate = moment(date).subtract(1, 'days')
    }
    setDate(newDate)
    toolbar.onNavigate('PREV')
  }
  const goToNext = () => {
    let newDate: any = ''
    if (view === 'month') {
      newDate = moment(date).add(1, 'months').startOf('month')
    } else if (view === 'week') {
      newDate = moment(date).add(1, 'week')
    } else if (view === 'day') {
      newDate = moment(date).add(1, 'days')
    }
    setDate(newDate)
    toolbar.onNavigate('NEXT')
  }

  const goToToday = () => {
    setDate(moment())
    toolbar.onNavigate('TODAY')
  }

  const handleViewChange = (view) => {
    setView(view)
    toolbar.onView(view)
  }

  return (
    <div className="flex flex-col-reverse sm:flex-row justify-between items-center mb-5">
      <div className="flex justify-center sm:justify-start items-center grow gap-3 mt-5 sm:mt-0">
        <button
          type="button"
          aria-label={t('calendar.back')}
          onClick={() => goToBack()}
          className="bg-transparent border-0 p-0 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <BackIcon className="fill-primary w-[45px] h-[45px] cursor-pointer" />
        </button>
        <div className="text-primary-text text-2xl font-normal leading-7 font-primary">
          {toolbar.label}
        </div>
        <button
          type="button"
          aria-label={t('calendar.next')}
          onClick={() => goToNext()}
          className="bg-transparent border-0 p-0 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <NextIcon className="fill-primary w-[45px] h-[45px] cursor-pointer" />
        </button>
        <button
          type="button"
          onClick={goToToday}
          className="ml-2 px-4 py-1.5 text-sm whitespace-nowrap rounded-full border border-primary text-primary bg-transparent hover:bg-primary-brandLight focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          {t('calendar.today')}
        </button>
      </div>
      <CalendarField>
        <SelectField
          placeholder={t('chatbot.form.pleaseSelect')}
          value={view}
          name="calendarView"
          options={localizedCalendarOptions}
          onChange={handleViewChange}
        />
      </CalendarField>
    </div>
  )
}

export default Toolbar
